import React, { useState } from 'react';

// Ensure media URL is absolute and works for preview (fixes BASE_URL/domain mismatch).
// Meta CDN URLs (scontent.whatsapp.net) are routed through our proxy to bypass CORS.
const toAbsoluteMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (typeof window === 'undefined') return url;
  if (url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return window.location.origin + url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const u = new URL(url);
      const pathAndQuery = u.pathname + u.search;
      // Route Meta CDN URLs through our server proxy to avoid CORS blocks
      if (/\.(whatsapp\.net|fbcdn\.net)$/i.test(u.host)) {
        return `${window.location.origin}/api/whatsapp/cdn-proxy?url=${encodeURIComponent(url)}`;
      }
      const isOurMediaPath = pathAndQuery.includes('/api/whatsapp/media/');
      const isLocalhost = /^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(u.host);
      const isOldRenderDomain = /real-crm\.onrender\.com$/i.test(u.host);
      const weAreProduction = !/^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(window.location.host);
      if (isOurMediaPath && weAreProduction && (isLocalhost || isOldRenderDomain)) {
        return window.location.origin + pathAndQuery;
      }
    } catch (_) {}
    return url;
  }
  return url;
};

const SAMPLE_PRESETS = [
  { id: 'ahmed', name: 'أحمد', area: 'الرياض', propertyType: 'شقة' },
  { id: 'sara', name: 'سارة', area: 'جدة', propertyType: 'فيلا' },
  { id: 'guest', name: 'عميل', area: '—', propertyType: '—' },
  { id: 'khalid', name: 'خالد', area: 'العليا', propertyType: 'شقة' }
];

const replaceVariables = (text, sample) => {
  if (!text) return '';
  return text
    .replace(/\{\{1\}\}/g, sample?.name || 'أحمد')
    .replace(/\{\{2\}\}/g, sample?.area || '...')
    .replace(/\{\{3\}\}/g, sample?.propertyType || '...')
    .replace(/\{\{\d+\}\}/g, '—');
};

const detectTextDirection = (value = '') => {
  const text = String(value || '');
  const hasArabic = /[\u0600-\u06FF]/.test(text);
  return hasArabic ? 'rtl' : 'ltr';
};

// WhatsApp system font stack — matches actual WhatsApp on phones, not Real brand fonts
const whatsappFontFamily = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Render WhatsApp-style formatting: *bold*, _italic_, ~strikethrough~
// Splits on newlines first so <br> tags are inserted between lines,
// then processes each line for inline format tokens.
function renderFormattedText(rawText) {
  if (!rawText) return null;
  const lines = String(rawText).split('\n');
  const output = [];
  for (let li = 0; li < lines.length; li++) {
    if (li > 0) output.push(<br key={`br${li}`} />);
    const line = lines[li];
    // regex matches *bold*, _italic_, ~strike~ — greedy single-char delimiters
    const re = /\*([^*]+)\*|_([^_]+)_|~([^~]+)~/g;
    let cursor = 0;
    let m;
    while ((m = re.exec(line)) !== null) {
      // plain text before match
      if (m.index > cursor) {
        output.push(<span key={`${li}p${cursor}`}>{line.slice(cursor, m.index)}</span>);
      }
      if (m[1] !== undefined) {
        output.push(<strong key={`${li}b${m.index}`} style={{ fontWeight: 700 }}>{m[1]}</strong>);
      } else if (m[2] !== undefined) {
        output.push(<em key={`${li}i${m.index}`}>{m[2]}</em>);
      } else if (m[3] !== undefined) {
        output.push(<s key={`${li}s${m.index}`}>{m[3]}</s>);
      }
      cursor = m.index + m[0].length;
    }
    // remainder of line after last match
    if (cursor < line.length) {
      output.push(<span key={`${li}p${cursor}`}>{line.slice(cursor)}</span>);
    }
  }
  return output;
}

const WhatsAppTemplatePreview = ({
  bodyText = '',
  layout = 'standard',
  carouselCards = [],
  carouselQuickReplyText = '',
  carouselUrlButtonText = '',
  carouselUrl = '',
  headerFormat = '',
  headerText = '',
  headerMediaUrl = '',
  footerText = '',
  buttonType = '',
  buttonUrlText = '',
  buttonUrl = '',
  buttonPhoneText = '',
  buttonPhoneNumber = '',
  buttonSecondaryUrlText = '',
  buttonSecondaryUrl = '',
  buttonSecondaryPhoneText = '',
  buttonSecondaryPhoneNumber = '',
  buttonQuick = [],
  language = 'ar',
  showSampleSelector = true,
  samplePreset = null,
  onSampleChange,
  compact = false,
  phoneFrame = true,
  createStep = null
}) => {
  const [activePreset, setActivePreset] = useState(samplePreset || 'ahmed');
  const [mediaLoadError, setMediaLoadError] = useState(false);
  const sample = SAMPLE_PRESETS.find((p) => p.id === activePreset) || SAMPLE_PRESETS[0];
  const resolvedBody = replaceVariables(bodyText, sample);
  const textDirection = detectTextDirection(resolvedBody);
  const mediaUrl = toAbsoluteMediaUrl(headerMediaUrl);
  const isCarousel = String(layout || '').toLowerCase() === 'carousel';
  const bt = (buttonType || '').toLowerCase();
  const ctaButtons = (() => {
    const list = [];
    if (bt === 'url') {
      list.push({ type: 'url', text: buttonUrlText || (language === 'ar' ? 'زر الرابط' : 'URL button') });
    } else if (bt === 'phone') {
      list.push({ type: 'phone', text: buttonPhoneText || (language === 'ar' ? 'زر الاتصال' : 'Call button') });
    } else if (bt === 'url_phone') {
      list.push({ type: 'url', text: buttonUrlText || (language === 'ar' ? 'زر الرابط' : 'URL button') });
      list.push({ type: 'phone', text: buttonSecondaryPhoneText || (language === 'ar' ? 'زر الاتصال' : 'Call button') });
    } else if (bt === 'phone_url') {
      list.push({ type: 'phone', text: buttonPhoneText || (language === 'ar' ? 'زر الاتصال' : 'Call button') });
      list.push({ type: 'url', text: buttonSecondaryUrlText || (language === 'ar' ? 'زر الرابط' : 'URL button') });
    }
    return list;
  })();

  // Reset load error when URL or step changes (retry when navigating)
  React.useEffect(() => { setMediaLoadError(false); }, [mediaUrl]);
  React.useEffect(() => { if (createStep != null) setMediaLoadError(false); }, [createStep]);

  const handlePresetClick = (id) => {
    setActivePreset(id);
    onSampleChange?.(SAMPLE_PRESETS.find((p) => p.id === id));
  };

  const bubbleContent = (
    <div style={{
      padding: headerFormat && !isCarousel ? '10px 16px 14px' : '12px 16px',
      background: '#dcf8c6',
      borderRadius: '12px 12px 4px 12px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1), 0 2px 12px rgba(0,0,0,0.08)',
      maxWidth: '280px',
      minWidth: '180px',
      position: 'relative'
    }}>
      {!isCarousel && headerFormat && (
        <div style={{
          marginBottom: '8px',
          height: headerFormat === 'IMAGE' || headerFormat === 'VIDEO' ? '100px' : '48px',
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: mediaUrl ? '#e8e8e8' : 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
          fontSize: '11px',
          color: mediaUrl ? 'transparent' : '#2e7d32'
        }}>
          {headerFormat === 'TEXT' ? (
            <div style={{
              width: '100%',
              padding: '10px 12px',
              color: '#0f172a',
              fontWeight: 700,
              fontSize: '13px',
              textAlign: 'left',
              direction: detectTextDirection(headerText),
              whiteSpace: 'pre-wrap'
            }}>
              {headerText || (language === 'ar' ? 'نص رأس الرسالة' : 'Header text')}
            </div>
          ) : mediaUrl && headerFormat === 'IMAGE' && !mediaLoadError ? (
            <img key={mediaUrl} src={mediaUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={() => setMediaLoadError(true)} referrerPolicy="no-referrer" />
          ) : mediaUrl && headerFormat === 'VIDEO' && !mediaLoadError ? (
            <video key={mediaUrl} src={mediaUrl} muted autoPlay loop playsInline preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={() => setMediaLoadError(true)} />
          ) : mediaUrl && headerFormat === 'DOCUMENT' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: '#2e7d32' }}>
              <span style={{ fontSize: '24px' }}>📄</span>
              <span style={{ fontSize: '10px', fontFamily: whatsappFontFamily }}>{language === 'ar' ? 'مستند' : 'Document'}</span>
            </div>
          ) : mediaLoadError && mediaUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', color: '#64748b', fontSize: '11px', fontFamily: whatsappFontFamily }}>
              <span style={{ fontSize: '20px' }}>{headerFormat === 'IMAGE' ? '🖼' : '🎬'}</span>
              <span>{language === 'ar' ? 'الصورة مرفقة' : 'Image attached'}</span>
            </div>
          ) : (
            headerFormat === 'IMAGE' ? '🖼' : headerFormat === 'VIDEO' ? '🎬' : '📄'
          )}
        </div>
      )}
      <div style={{ fontSize: '14px', color: resolvedBody ? '#111' : '#64748b', lineHeight: 1.6, fontStyle: resolvedBody ? 'normal' : 'italic', direction: textDirection, textAlign: textDirection === 'rtl' ? 'right' : 'left', wordBreak: 'break-word', fontFamily: whatsappFontFamily }}>
        {resolvedBody ? renderFormattedText(resolvedBody) : (language === 'ar' ? 'ستظهر رسالتك هنا...' : 'Your message will appear here...')}
      </div>
      {isCarousel && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ fontSize: '10px', fontWeight: '500', color: '#94a3b8', marginBottom: '6px', lineHeight: 1.35, fontFamily: whatsappFontFamily }}>
            {language === 'ar'
              ? 'معاينة تقريبية: نفس الأزرار تتكرر تحت كل صورة (كما في واتساب).'
              : 'Approximate preview: the same buttons repeat under each image (like WhatsApp).'}
          </div>
          <div style={{
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            paddingBottom: '6px',
            marginLeft: '-2px',
            marginRight: '-2px',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
          }}
          >
            {(Array.isArray(carouselCards) ? carouselCards : []).map((c, i) => {
              const cardUrl = toAbsoluteMediaUrl(c?.media_url || '');
              const qr = carouselQuickReplyText?.trim();
              const urlLabel = carouselUrlButtonText?.trim() || (language === 'ar' ? 'رابط' : 'Link');
              return (
                <div
                  key={i}
                  style={{
                    flex: '0 0 118px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    background: '#fff',
                    border: '1px solid #d1d7db',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div style={{
                    height: '72px',
                    flexShrink: 0,
                    background: cardUrl ? '#e8e8e8' : 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                    color: '#2e7d32',
                  }}
                  >
                    {cardUrl ? (
                      <img src={cardUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} referrerPolicy="no-referrer" />
                    ) : (
                      '🖼'
                    )}
                  </div>
                  {/* Meta: same buttons on every card; order matches API (quick_reply then URL). */}
                  <div style={{ padding: '5px 5px 7px', display: 'flex', flexDirection: 'column', gap: '5px', flex: 1, background: '#f0f2f5' }}>
                    {qr && (
                      <div style={{ padding: '4px 6px', background: '#fff', borderRadius: '14px', fontSize: '9px', color: '#111b21', fontWeight: '500', textAlign: 'center', lineHeight: 1.25, wordBreak: 'break-word', border: '1px solid #e9edef', fontFamily: whatsappFontFamily }}>
                        {qr}
                      </div>
                    )}
                    <div style={{ padding: '6px 6px', background: '#fff', borderRadius: '6px', fontSize: '10px', color: '#008069', fontWeight: '600', textAlign: 'center', lineHeight: 1.25, wordBreak: 'break-word', border: '1px solid #e9edef', fontFamily: whatsappFontFamily }}>
                      {urlLabel}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Footer - comes before buttons (WhatsApp standard) */}
      {!isCarousel && footerText && (
        <div style={{ marginTop: '8px', fontSize: '11px', color: '#64748b', direction: detectTextDirection(footerText), textAlign: detectTextDirection(footerText) === 'rtl' ? 'right' : 'left', whiteSpace: 'pre-wrap', fontFamily: whatsappFontFamily }}>
          {footerText}
        </div>
      )}
      {/* Buttons - CTA comes last (WhatsApp standard & UX best practice) */}
      {!isCarousel && ctaButtons.length > 0 && (
        <div style={{ marginTop: '8px', display: 'grid', gap: '6px' }}>
          {ctaButtons.map((btn, idx) => (
            <div key={`cta-${idx}`} style={{ padding: '10px 14px', background: '#25D366', borderRadius: '8px', fontSize: '13px', color: '#fff', fontWeight: '500', textAlign: 'center', opacity: btn.text ? 1 : 0.7, fontFamily: whatsappFontFamily }}>
              {btn.text}
            </div>
          ))}
        </div>
      )}
      {!isCarousel && bt === 'quick_reply' && (
        <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {buttonQuick.filter(Boolean).length > 0 ? (
            buttonQuick.filter(Boolean).map((t, i) => (
              <span key={i} style={{ padding: '8px 14px', background: '#e9ecef', borderRadius: '20px', fontSize: '13px', color: '#1c1e21', fontWeight: '500', fontFamily: whatsappFontFamily }}>
                {t}
              </span>
            ))
          ) : (
            <span style={{ padding: '8px 14px', background: '#e9ecef', borderRadius: '20px', fontSize: '13px', color: '#64748b', fontWeight: '500', fontStyle: 'italic', fontFamily: whatsappFontFamily }}>
              {language === 'ar' ? 'أزرار رد سريع' : 'Quick reply buttons'}
            </span>
          )}
        </div>
      )}
    </div>
  );

  const chatHeader = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 14px',
      background: 'linear-gradient(180deg, #075e54 0%, #054d47 100%)'
    }}>
      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>✓</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#fff' }}>{language === 'ar' ? 'نفوذ التطوير' : 'Nafooth Development'}</div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#34d399', boxShadow: '0 0 6px rgba(52,211,153,0.6)' }} />
          {language === 'ar' ? 'متصل' : 'online'}
        </div>
      </div>
    </div>
  );

  const chatArea = (
    <div style={{
      background: 'linear-gradient(180deg, #e5ddd5 0%, #d4c9be 100%), url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 30m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0\' fill=\'%23000000\' fill-opacity=\'0.03\'/%3E%3C/svg%3E")',
      minHeight: compact ? 'auto' : '240px',
      padding: compact ? '12px' : '16px 12px',
      borderRadius: compact ? '0 0 12px 12px' : '12px',
      position: 'relative'
    }}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '0 4px' }}>
        {bubbleContent}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', marginLeft: '8px', fontSize: '11px', color: 'rgba(0,0,0,0.45)', fontFamily: whatsappFontFamily }}>
        <span>{new Date().toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en', { hour: '2-digit', minute: '2-digit' })}</span>
        <span style={{ color: '#53bdeb', fontSize: '14px', lineHeight: 1 }}>✓✓</span>
      </div>
    </div>
  );

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {showSampleSelector && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
            {language === 'ar' ? 'معاينة كـ:' : 'Preview as:'}
          </span>
          {SAMPLE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handlePresetClick(p.id)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                borderRadius: '9999px',
                border: activePreset === p.id ? '2px solid #25D366' : '1px solid #e2e8f0',
                background: activePreset === p.id ? '#dcfce7' : '#fff',
                color: activePreset === p.id ? '#15803d' : '#64748b',
                cursor: 'pointer',
                fontWeight: activePreset === p.id ? '600' : '500',
                transition: 'all 0.2s ease',
                boxShadow: activePreset === p.id ? '0 2px 8px rgba(37,211,102,0.2)' : 'none'
              }}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}
      {compact ? (
        <>
          {chatHeader}
          {chatArea}
        </>
      ) : phoneFrame ? (
        <div style={{
          background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 100%)',
          borderRadius: '36px',
          padding: '12px',
          boxShadow: '0 30px 60px -15px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
          width: 'min(280px, 100%)',
          margin: '0 auto'
        }}>
          {/* Phone notch */}
          <div style={{
            width: '90px',
            height: '22px',
            background: '#0a0a0a',
            borderRadius: '0 0 14px 14px',
            margin: '-2px auto 10px',
            position: 'relative'
          }} />
          {/* Phone screen */}
          <div style={{
            background: '#fff',
            borderRadius: '24px',
            overflow: 'hidden',
            minHeight: '360px',
            boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.06)'
          }}>
            {/* Status bar */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 18px 6px',
              fontSize: '13px',
              fontWeight: '600',
              color: '#000',
              background: '#fff'
            }}>
              <span>{new Date().toLocaleTimeString(language === 'ar' ? 'ar-SA' : 'en', { hour: '2-digit', minute: '2-digit' })}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
                <span>📶</span>
                <span>🔋</span>
              </div>
            </div>
            {chatHeader}
            {chatArea}
          </div>
        </div>
      ) : (
        <div style={{
          borderRadius: '16px',
          overflow: 'hidden',
          maxWidth: '400px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
        }}>
          {chatHeader}
          {chatArea}
        </div>
      )}
    </div>
  );

  return content;
};

export default WhatsAppTemplatePreview;
